import React, { useState, useEffect, useMemo } from 'react';

interface ResponsiveRouteProps {
    mobile: React.ReactElement;
    desktop: React.ReactElement;
}

/**
 * Component that renders different content based on screen size
 * Prevents flash of wrong content by initializing synchronously
 */
const ResponsiveRoute: React.FC<ResponsiveRouteProps> = ({ mobile, desktop }) => {
    // Initialize synchronously to prevent flash
    const [isMobile, setIsMobile] = useState(() => {
        if (typeof window !== 'undefined') {
            return window.innerWidth < 1024;
        }
        return false;
    });

    // Track if component has mounted to prevent hydration mismatches
    const [mounted, setMounted] = useState(false);

    useEffect(() => {
        setMounted(true);

        const checkMobile = () => {
            const mobileCheck = window.innerWidth < 1024;
            setIsMobile(mobileCheck);
        };

        const handleOrientationChange = () => {
            // Small delay to ensure orientation change is complete
            setTimeout(checkMobile, 100);
        };

        // Initial check after mount
        checkMobile();

        // Listen for resize and orientation changes
        window.addEventListener('resize', checkMobile);
        window.addEventListener('orientationchange', handleOrientationChange);

        return () => {
            window.removeEventListener('resize', checkMobile);
            window.removeEventListener('orientationchange', handleOrientationChange);
        };
    }, []);

    // Return the appropriate component based on screen size
    // Use useMemo to prevent unnecessary re-renders
    return useMemo(() => {
        return isMobile ? mobile : desktop;
    }, [isMobile, mobile, desktop]);
};

export default ResponsiveRoute;
